import { Link } from 'react-router-dom';
import { Icon, type IconName } from './ui';

/**
 * The way into every other module.
 *
 * DECISION — gradient tiles, not photos. Photos in this app are the family's own
 * (meal shots in R2); there are none on day one, so a photo tile grid would ship
 * as seven gray rectangles and stay that way. Warm gradients built from the
 * palette tokens are honest on day one and instant on a phone. Each tile carries
 * a live line of real data instead of decoration, which is the part that actually
 * makes it worth tapping. To switch to photos later: add `image?: string` to Tile
 * and render it behind the same content — nothing else changes.
 */
export interface Tile {
  to: string;
  label: string;
  /** A live line of data. Never a static tagline. */
  hint: string;
  icon: IconName;
  gradient: string;
  accent: string;
}

export const TILE_STYLES = {
  money: { icon: 'wallet' as IconName, gradient: 'from-sage-soft to-surface', accent: 'text-sage' },
  meals: { icon: 'pot' as IconName, gradient: 'from-honey-soft to-surface', accent: 'text-honey' },
  chores: { icon: 'broom' as IconName, gradient: 'from-clay-soft to-surface', accent: 'text-clay' },
  calendar: {
    icon: 'calendar' as IconName,
    gradient: 'from-dusk-soft to-surface',
    accent: 'text-dusk',
  },
  location: { icon: 'pin' as IconName, gradient: 'from-sage-soft to-surface', accent: 'text-sage' },
  house: { icon: 'key' as IconName, gradient: 'from-honey-soft to-surface', accent: 'text-honey' },
  insurance: {
    icon: 'shield' as IconName,
    gradient: 'from-dusk-soft to-surface',
    accent: 'text-dusk',
  },
  settings: {
    icon: 'gear' as IconName,
    gradient: 'from-surface-sunk to-surface',
    accent: 'text-ink-soft',
  },
  notifications: {
    icon: 'bell' as IconName,
    gradient: 'from-dusk-soft to-surface',
    accent: 'text-dusk',
  },
  birthdays: {
    icon: 'calendar' as IconName,
    gradient: 'from-clay-soft to-surface',
    accent: 'text-clay',
  },
  reminders: {
    icon: 'check' as IconName,
    gradient: 'from-honey-soft to-surface',
    accent: 'text-honey',
  },
  ask: { icon: 'pulse' as IconName, gradient: 'from-sage-soft to-surface', accent: 'text-sage' },
} as const;

export default function ModuleTiles({ tiles }: { tiles: Tile[] }) {
  return (
    <nav aria-label="Household sections">
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {tiles.map((tile) => (
          <li key={tile.to}>
            <Link
              to={tile.to}
              className={`flex h-full min-h-28 flex-col justify-between gap-3 rounded-card border border-border bg-gradient-to-br p-4 transition-shadow hover:shadow-[0_2px_10px_rgba(43,39,35,0.08)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage ${tile.gradient}`}
            >
              <span className={tile.accent}>
                <Icon name={tile.icon} className="h-6 w-6" />
              </span>
              <span>
                <span className="block font-display text-lg font-semibold text-ink">
                  {tile.label}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-ink-soft">{tile.hint}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
